import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import toast, { LoaderIcon } from "react-hot-toast";
import {
  approveItem,
  deleteItem,
  getVenues,
  rejectItem,
} from "../../../api/getVenue";
import Modal from "../../shared/custom-modal/custom-modal";
import DynamicSVG from "./dynmaic-svg";
import { formatDate } from "../../../utils/format-date";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { FiFilter } from "react-icons/fi";

const rejectionReasons = [
  "Incomplete Application ",
  "Photos and Videos are not clear",
  "Inaccurate/Missing Information",
  "Other",
];

const STATUS_OPTIONS = [
  "Approved",
  "Pending",
  "Rejected",
  "ReSubmitting",
  "Edit Pending",
  "unpublish",
];

const VENUE_STATUS_OPTIONS = [
  "Draft",
  "Under Review",
  "Edits Under Review",
  "Live",
  "Rejected",
  "Unpublished",
];

const TextFilterPanel = ({
  opRef,
  label,
  value,
  onChange,
  onClear,
  onApply,
  placeholder = "Type to filter...",
}) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <OverlayPanel
      ref={opRef}
      showCloseIcon
      dismissable={!isEditing}
      className="p-3 w-64"
    >
      <div
        className="flex flex-col gap-2"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onScroll={(e) => e.stopPropagation()}
      >
        <label className="text-xs text-gray-600">{label}</label>
        <input
          value={value}
          onFocus={() => setIsEditing(true)}
          onBlur={() => setIsEditing(false)}
          onChange={(e) => {
            // No preventDefault needed; stopping propagation is sufficient
            e.stopPropagation();
            onChange(e.target.value);
          }}
          className="border rounded px-2 py-2 text-sm"
          placeholder={placeholder}
        />
        <div className="flex gap-2 justify-end mt-2">
          <button
            className="px-3 py-1.5 text-sm rounded border bg-gray-50 hover:bg-gray-100"
            onClick={() => {
              onClear();
              opRef.current?.hide();
            }}
          >
            Clear
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded bg-primary text-white hover:brightness-95"
            onClick={() => {
              onApply();
              opRef.current?.hide();
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </OverlayPanel>
  );
};

const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);


  return debouncedValue;
}
const VenuesTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [apiFilter, setApiFilter] = useState({ criteria: [] });

  const [statusFilter, setStatusFilter] = useState(null);
  const [venueStatusFilter, setVenueStatusFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  const statusOp = useRef(null);
  const venueStatusOp = useRef(null);
  const categoryOp = useRef(null);
  const nameOp = useRef(null);
  const locationOp = useRef(null);
  const emailOp = useRef(null);

  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [globalSearch, setGlobalSearch] = useState(null);


  const debounchedSearch = useDebounce(globalSearch);

  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["venueData", { page: currentPage, limit: itemsPerPage, filter: apiFilter }],
    queryFn: () => getVenues(currentPage, itemsPerPage, apiFilter, debounchedSearch),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    refetch();
  }, [debounchedSearch])

  const forms = data?.forms ?? [];
  const totalCount = data?.totalCount ?? 0;

  const deleteMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["venueData"] });
      toast.success("Item deleted successfully");
    },
    onError: (error) => console.error("Error deleting:", error?.message),
  });

  const approveMutation = useMutation({
    mutationFn: approveItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["venueData"] });
      toast.success("Item approved successfully");
    },
    onError: (error) => console.error("Error approving:", error?.message),
  });

  const rejectMutation = useMutation({
    mutationFn: rejectItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["venueData"] });
      setModalOpen(false);
      toast.success("Item rejected successfully");
    },
    onError: (error) => console.error("Error rejecting:", error?.message),
  });

  const upsertCriterion = (criteria, key, value) => {
    const filtered = criteria.filter((c) => c.key !== key);
    if (value !== null && value !== undefined && value !== "") {
      filtered.push({ key, value, type: "regexOr" });
    }
    return filtered;
  };

  const applyFilterAndRefetch = (newCriteria) => {
    setApiFilter({ criteria: newCriteria });
    setCurrentPage(1);
    queueMicrotask(() => refetch());
  };

  const handleDelete = async (category, id) => {
    await deleteMutation.mutateAsync({ category, id });
    refetch();
  };

  const handleApprove = async (item) => {
    try {
      setIsApproving(true);
      await approveMutation.mutateAsync({
        category: item?.category,
        id: item._id,
        email: item?.vendorEmail,
        name:
          item?.category === "Hotel"
            ? item?.overview?.basicDetails?.hotelName
            : item?.basicDetails?.venueName,
      });
    } catch {
      toast.error("Error approving item");
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    const updatedReason = [...selectedReasons];
    if (rejectionReason) updatedReason.push(rejectionReason);
    if (updatedReason.length === 0) {
      toast.error("Please select at least one reason");
      return;
    }
    try {
      setIsRejecting(true);
      await rejectMutation.mutateAsync({
        category: selectedItem.category,
        id: selectedItem._id,
        reasons: updatedReason,
        email: selectedItem?.vendorEmail,
        name:
          selectedItem?.category === "Hotel"
            ? selectedItem?.overview?.basicDetails?.hotelName
            : selectedItem?.basicDetails?.venueName,
      });
    } catch {
      toast.error("Error rejecting");
    } finally {
      setRejectionReason("");
      setSelectedReasons([]);
      setIsRejecting(false);
    }
  };

  const statusBodyTemplate = (item) => (
    <span
      className={`font-semibold ${
        item.itemStatus === "Approved"
          ? "text-green-500"
          : item.itemStatus === "Rejected"
          ? "text-red-500"
          : "text-blue-500"
      }`}
      title={item.itemStatus || "Pending"}
    >
      {item.itemStatus || "Pending"}
    </span>
  );

  const statusClasses = {
    Draft: "bg-[#EBEBEB] text-[#000000]",
    underReview: "bg-[#DFDFFF] text-[#4A49D9]",
    editsunderReview: "bg-[#DFDFFF] text-[#4A49D9]",
    live: "bg-[#EAF9F7] text-[#039F8D]",
    rejected: "bg-red-200 text-red-800",
    default: "bg-gray-200 text-gray-800",
  };

  const getStatusText = (status, itemStatus) => {
    switch (true) {
      case status?.toLowerCase?.() === "pending" &&
        itemStatus?.toLowerCase?.() === "pending":
        return "Draft";
      case status === "completed" &&
        ["pending", "Pending", "ReSubmitting"].includes(itemStatus):
        return "Under Review";
      case status === "completed" && itemStatus === "Edit Pending":
        return "Edits Under Review";
      case status === "completed" && itemStatus === "Approved":
        return "Live";
      case status === "completed" && itemStatus === "Rejected":
        return "Rejected";
      case status === "completed" && itemStatus === "unpublish":
        return "Unpublished";
      default:
        return "Draft";
    }
  };

  const getStatusClass = (status, itemStatus) => {
    switch (true) {
      case status === "pending" && itemStatus === "pending":
        return statusClasses.Draft;
      case status === "completed" &&
        ["pending", "Pending", "ReSubmitting"].includes(itemStatus):
        return statusClasses.underReview;
      case status === "completed" && itemStatus === "Approved":
        return statusClasses.live;
      case status === "completed" && itemStatus === "Edit Pending":
        return statusClasses.editsunderReview;
      case status === "completed" && itemStatus === "Rejected":
        return statusClasses.rejected;
      default:
        return statusClasses.default;
    }
  };

  const venueStatusBodyTemplate = (item) => {
    const text = getStatusText(item?.status, item?.itemStatus);
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs md:text-sm ${getStatusClass(
          item?.status,
          item?.itemStatus
        )}`}
        style={{
          maxWidth: 200,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={text}
      >
        {text}
      </span>
    );
  };

  const dateBodyTemplate = (item) =>
    item?.updatedAt ? formatDate(item.updatedAt) : formatDate(item.createdAt);

  const nameBodyTemplate = (item) => {
    const val =
      item.category === "Hotel"
        ? item.overview?.basicDetails?.hotelName
        : item.basicDetails?.venueName;
    return (
      <span
        className="block text-xs md:text-sm"
        style={{
          maxWidth: 220,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={val || ""}
      >
        {val || ""}
      </span>
    );
  };

  const categoryBodyTemplate = (item) => (
    <span
      className="block text-xs md:text-sm"
      style={{
        maxWidth: 180,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
      title={item?.category || ""}
    >
      {item?.category || ""}
    </span>
  );

  const cityBodyTemplate = (item) => {
    const val =
      item.category === "Hotel"
        ? `${item.overview?.locationDetails?.city ?? ""} ${
            item.overview?.locationDetails?.area ?? ""
          }`.trim()
        : `${item.location?.city ?? ""} ${item.location?.area ?? ""}`.trim();
    return (
      <span
        className="block text-xs md:text-sm"
        style={{
          maxWidth: 240,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={val}
      >
        {val}
      </span>
    );
  };

  const emailBodyTemplate = (item) => (
    <span
      className="block text-xs md:text-sm"
      style={{
        maxWidth: 260,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
      title={item?.vendorEmail || ""}
    >
      {item?.vendorEmail || ""}
    </span>
  );

  const docBodyTemplate = (item) => (
    <Link
      to={`/doc-preview/${item.category}/${item._id}`}
      className="bg-blue-400 text-white text-center px-3 py-1 rounded-md shadow block w-[7rem]"
    >
      View Doc
    </Link>
  );

  const previewBodyTemplate = (item) => (
    <Link
      to={`/${
        item.category === "Hotel" ? "hotel-preview" : "venue-preview"
      }/${item.category}/${item._id}`}
      className="bg-primary text-white px-3 py-1 rounded-md shadow"
    >
      Preview
    </Link>
  );

  const actionBodyTemplate = (item) => (
    <div className="flex gap-2">
      {item.itemStatus !== "Approved" && item.itemStatus !== "Rejected" && (
        <>
          <Button
            label="Approve"
            icon="pi pi-check"
            className="p-button-success p-button-sm"
            onClick={() => handleApprove(item)}
          />
          <Button
            label="Reject"
            icon="pi pi-times"
            className="p-button-danger p-button-sm"
            onClick={() => {
              setSelectedItem(item);
              setModalOpen(true);
            }}
          />
        </>
      )}
    </div>
  );

  const StatusHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Status</span>
      <button
        type="button"
        onClick={(e) => statusOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${
          statusFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"
        }`}
        aria-label="Filter by status"
        title="Filter by status"
      >
        <FiFilter />
      </button>
      {statusFilter && (
        <span className="text-[10px] md:text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
          {statusFilter}
        </span>
      )}
      <OverlayPanel ref={statusOp} showCloseIcon dismissable className="p-2 w-56">
        <div
          className="flex flex-col gap-1"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <button
            className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 ${!statusFilter ? "bg-gray-100" : ""}`}
            onClick={() => {
              setStatusFilter(null);
              const next = apiFilter.criteria.filter((c) => c.key !== "itemStatus");
              applyFilterAndRefetch(next);
              statusOp.current?.hide();
            }}
          >
            All Statuses
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 ${statusFilter === s ? "bg-gray-100" : ""}`}
              onClick={() => {
                setStatusFilter(s);
                const next = upsertCriterion(
                  apiFilter.criteria.filter((c) => c.key !== "itemStatus"),
                  "itemStatus",
                  s
                );
                applyFilterAndRefetch(next);
                statusOp.current?.hide();
              }}
            >
              <span
                className={`${
                  s === "Approved"
                    ? "text-green-600"
                    : s === "Rejected"
                    ? "text-red-600"
                    : "text-blue-600"
                }`}
              >
                {s}
              </span>
            </button>
          ))}
        </div>
      </OverlayPanel>
    </div>
  );

  const VenueStatusHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Venue Status</span>
      <button
        type="button"
        onClick={(e) => venueStatusOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${
          venueStatusFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"
        }`}
        aria-label="Filter by venue status"
        title="Filter by venue status"
      >
        <FiFilter />
      </button>
      {venueStatusFilter && (
        <span className="text-[10px] md:text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
          {venueStatusFilter}
        </span>
      )}
      <OverlayPanel ref={venueStatusOp} showCloseIcon dismissable className="p-2 w-56">
        <div
          className="flex flex-col gap-2"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col">
            <button
              className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 ${!venueStatusFilter ? "bg-gray-100" : ""}`}
              onClick={() => {
                setVenueStatusFilter(null);
                const next = apiFilter.criteria.filter((c) => c.key !== "venueStatus");
                applyFilterAndRefetch(next);
                venueStatusOp.current?.hide();
              }}
            >
              All Venue Statuses
            </button>
            {VENUE_STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 ${venueStatusFilter === s ? "bg-gray-100" : ""}`}
                onClick={() => {
                  setVenueStatusFilter(s);
                  const next = upsertCriterion(
                    apiFilter.criteria.filter((c) => c.key !== "venueStatus"),
                    "venueStatus",
                    s
                  );
                  applyFilterAndRefetch(next);
                  venueStatusOp.current?.hide();
                }}
              >
                <span className="text-blue-600">{s}</span>
              </button>
            ))}
          </div>
        </div>
      </OverlayPanel>
    </div>
  );

  const CategoryHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Category</span>
      <button
        type="button"
        onClick={(e) => categoryOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${
          categoryFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"
        }`}
        aria-label="Filter by category"
        title="Filter by category"
      >
        <FiFilter />
      </button>
      <TextFilterPanel
        opRef={categoryOp}
        label="Category"
        value={categoryFilter}
        onChange={setCategoryFilter}
        onClear={() => {
          setCategoryFilter("");
          const next = upsertCriterion(apiFilter.criteria, "category", "");
          applyFilterAndRefetch(next);
        }}
        onApply={() => {
          const next = upsertCriterion(apiFilter.criteria, "category", categoryFilter.trim());
          applyFilterAndRefetch(next);
        }}
        placeholder="e.g., Hotel, Venue"
      />
    </div>
  );

  const NameHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Name of Premises</span>
      <button
        type="button"
        onClick={(e) => nameOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${
          nameFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"
        }`}
        aria-label="Filter by name"
        title="Filter by name"
      >
        <FiFilter />
      </button>
      <TextFilterPanel
        opRef={nameOp}
        label="Name of Premises"
        value={nameFilter}
        onChange={setNameFilter}
        onClear={() => {
          setNameFilter("");
          const next = upsertCriterion(apiFilter.criteria, "name", "");
          applyFilterAndRefetch(next);
        }}
        onApply={() => {
          const next = upsertCriterion(apiFilter.criteria, "name", nameFilter.trim());
          applyFilterAndRefetch(next);
        }}
        placeholder="Enter full or partial name"
      />
    </div>
  );

  const LocationHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Location (City)</span>
      <button
        type="button"
        onClick={(e) => locationOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${
          locationFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"
        }`}
        aria-label="Filter by location"
        title="Filter by location"
      >
        <FiFilter />
      </button>
      <TextFilterPanel
        opRef={locationOp}
        label="Location"
        value={locationFilter}
        onChange={setLocationFilter}
        onClear={() => {
          setLocationFilter("");
          const next = upsertCriterion(apiFilter.criteria, "location", "");
          applyFilterAndRefetch(next);
        }}
        onApply={() => {
          const next = upsertCriterion(apiFilter.criteria, "location", locationFilter.trim());
          applyFilterAndRefetch(next);
        }}
        placeholder="City or Area"
      />
    </div>
  );

  const EmailHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Email</span>
      <button
        type="button"
        onClick={(e) => emailOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${
          emailFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"
        }`}
        aria-label="Filter by email"
        title="Filter by email"
      >
        <FiFilter />
      </button>
      <TextFilterPanel
        opRef={emailOp}
        label="Vendor Email"
        value={emailFilter}
        onChange={setEmailFilter}
        onClear={() => {
          setEmailFilter("");
          const next = upsertCriterion(apiFilter.criteria, "vendorEmail", "");
          applyFilterAndRefetch(next);
        }}
        onApply={() => {
          const next = upsertCriterion(apiFilter.criteria, "vendorEmail", emailFilter.trim());
          applyFilterAndRefetch(next);
        }}
        placeholder="user@domain.com"
      />
    </div>
  );

  return (
    <div className="p-6 relative">
      {(isApproving || isRejecting) && (
        <div className="flex fixed top-0 left-0 z-50 justify-center items-center min-w-full min-h-screen bg-black opacity-30 ">
          <LoaderIcon />
        </div>
      )}

      {/* <h2 className="text-xl font-bold mb-4">Venues</h2> */}
      <div className="flex items-center mb-4">
        <h2 className="text-xl font-bold">Venues</h2>

         {/**@Search */}
         <input 
          type={"text"}
          placeholder="Search venues...."
          value={globalSearch}
          onChange={e => setGlobalSearch(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm mx-3"

         />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">Loading...</div>
      ) : (
        <DataTable
          value={forms}
          paginator
          lazy
          rows={itemsPerPage}
          totalRecords={totalCount}
          first={(currentPage - 1) * itemsPerPage}
          onPage={(e) => {
            setCurrentPage(e.page + 1);
            setItemsPerPage(e.rows);
          }}
          rowsPerPageOptions={[10, 20, 30, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="{first} - {last} of {totalRecords}"
          loading={isLoading}
          responsiveLayout="scroll"
          className="p-datatable-striped"
        >
          <Column
            header="#"
            body={(rowData, { rowIndex }) => (
              <DynamicSVG
                index={rowIndex}
                currentPage={1}
                itemsPerPage={itemsPerPage}
              />
            )}
            style={{ width: "4rem" }}
          />

          <Column field="itemStatus" header={StatusHeader} body={statusBodyTemplate} />

          <Column header={VenueStatusHeader} body={venueStatusBodyTemplate} />

          <Column header={CategoryHeader} body={categoryBodyTemplate} />

          <Column header={NameHeader} body={nameBodyTemplate} />

          <Column header="Date Submitted" body={dateBodyTemplate} />

          <Column header="View Doc" body={docBodyTemplate} />

          <Column header="Preview" body={previewBodyTemplate} />

          <Column header={LocationHeader} body={cityBodyTemplate} />

          <Column header={EmailHeader} body={emailBodyTemplate} />

          <Column header="Actions" body={actionBodyTemplate} />
          
        </DataTable>
      )}

      <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen}>
        <h2 className="text-lg font-semibold mb-4">Rejection Reason</h2>
        <div className="space-y-2">
          {rejectionReasons.map((reason) => (
            <label key={reason} className="block">
              <input
                type="checkbox"
                value={reason}
                checked={selectedReasons.includes(reason)}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedReasons((prev) =>
                    prev.includes(value)
                      ? prev.filter((x) => x !== value)
                      : [...prev, value]
                  );
                }}
                className="mr-2"
              />
              {reason}
            </label>
          ))}
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
            className="w-full border border-gray-300 rounded-lg p-2"
            placeholder="Provide a reason (optional)"
          />
        </div>
        <div className="flex gap-4 mt-4">
          <button
            onClick={handleReject}
            className="px-4 py-2 bg-red-500 text-white rounded-lg"
          >
            Submit
          </button>
          <button
            onClick={() => {
              setRejectionReason("");
              setSelectedReasons([]);
              setModalOpen(false);
            }}
            className="px-4 py-2 bg-gray-300 rounded-lg"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default VenuesTable;
